import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { map, Observable } from 'rxjs';
import { Environment } from 'src/environments/environment.model';

import { signalStoreCustomerRequestOptions } from '../../shared/interceptors/signal-store-customer-context';
import { DownyMildewCropFieldsDataWrapperAPIResponse, GetDownyMildewCropFieldApiResponse } from '../models/api-models';
import {
  CropFieldLatestGrowthStage,
  GrowthStageRegistration,
  GrowthStageRegistrationRequest,
  GrowthStageRegistrationsResponse,
  GrowthStageRemovalRequest,
} from '../models/growth-stage.models';
import {
  DownyMildewObservationRegistration,
  DownyMildewObservationRegistrationRequest,
  DownyMildewObservationRemovalRequest,
  DownyMildewObservationRegistrationsResponse,
} from '../models/observation.models';
import { mapDownyMildewDataAPIResponse, mapDownyMildewDataAPIResponses } from '../models/mappers';
import { DownyMildewData } from '../models/models';

@Injectable({
  providedIn: 'root',
})
export class DownyMildewService {
  private readonly http = inject(HttpClient);
  private readonly env = inject<Environment>(EnvironmentInjector);

  private readonly baseUrl = `${this.env.APIM_BASE_URL}/arable-disease-integration-service/downy-mildew`;
  private readonly cropFieldsEndpoint = 'crop-fields';

  public getDownyMildewCropField(
    cropFieldId: number,
    from: Date,
    until: Date,
    cropYear: number,
    customerId?: number,
  ): Observable<DownyMildewData> {
    const fromDateString = this.formatDate(from);
    const untilDateString = this.formatDate(until);

    const url = `${this.baseUrl}/${cropFieldId}?cropYear=${cropYear}&fromDate=${fromDateString}&untilDate=${untilDateString}`;
    return this.http
      .get<GetDownyMildewCropFieldApiResponse>(url, signalStoreCustomerRequestOptions(customerId))
      .pipe(map((response) => mapDownyMildewDataAPIResponse(response.cropField)));
  }

  public getAllDownyMildewCropFields(
    from: Date,
    until: Date,
    cropYear: number,
    customerId?: number,
  ): Observable<DownyMildewData[]> {
    const fromDateString = this.formatDate(from);
    const untilDateString = this.formatDate(until);

    const url = `${this.baseUrl}/${this.cropFieldsEndpoint}?cropYear=${cropYear}&fromDate=${fromDateString}&untilDate=${untilDateString}`;
    return this.http
      .get<DownyMildewCropFieldsDataWrapperAPIResponse>(url, signalStoreCustomerRequestOptions(customerId))
      .pipe(map((response) => mapDownyMildewDataAPIResponses(response.cropFields)));
  }

  public recalculateDownyMildewAdvisories(cropFieldIds: number[]): Observable<void> {
    const url = `${this.baseUrl}/${this.cropFieldsEndpoint}/recalculate`;

    let params = new HttpParams();
    cropFieldIds.forEach((id) => {
      params = params.append('cropFieldIds', id.toString());
    });

    return this.http.post<void>(url, null, { params });
  }

  public registerGrowthStage(request: GrowthStageRegistrationRequest): Observable<void> {
    const url = `${this.baseUrl}/${this.cropFieldsEndpoint}/growth-stages`;
    return this.http.post<void>(url, request);
  }

  public registerObservation(request: DownyMildewObservationRegistrationRequest): Observable<void> {
    const url = `${this.baseUrl}/${this.cropFieldsEndpoint}/observations`;
    return this.http.post<void>(url, request);
  }

  public getObservationHistory(
    cropFieldIds: number[],
    cropYear: number,
    customerId?: number,
  ): Observable<DownyMildewObservationRegistration[]> {
    const url = `${this.baseUrl}/${this.cropFieldsEndpoint}/observations`;
    return this.http
      .get<DownyMildewObservationRegistrationsResponse>(url, {
        params: {
          cropFieldIds: cropFieldIds.map((id) => id.toString()),
          cropYear: cropYear.toString(),
        },
        ...signalStoreCustomerRequestOptions(customerId),
      })
      .pipe(map((response) => response.observationRegistrations));
  }

  public deleteGrowthStage(request: GrowthStageRemovalRequest): Observable<void> {
    const url = `${this.baseUrl}/${this.cropFieldsEndpoint}/growth-stages`;
    return this.http.delete<void>(url, {
      body: request,
    });
  }

  public deleteObservation(request: DownyMildewObservationRemovalRequest): Observable<void> {
    const url = `${this.baseUrl}/${this.cropFieldsEndpoint}/observations`;
    return this.http.delete<void>(url, {
      body: request,
    });
  }

  public getLatestGrowthStages(cropFieldIds: number[], customerId?: number): Observable<CropFieldLatestGrowthStage[]> {
    const url = `${this.baseUrl}/${this.cropFieldsEndpoint}/growth-stages/latest`;
    return this.http.get<CropFieldLatestGrowthStage[]>(url, {
      params: {
        cropFieldIds: cropFieldIds.map((id) => id.toString()),
      },
      ...signalStoreCustomerRequestOptions(customerId),
    });
  }

  public getGrowthStageHistory(
    cropFieldIds: number[],
    cropYear: number,
    customerId?: number,
  ): Observable<GrowthStageRegistration[]> {
    const url = `${this.baseUrl}/${this.cropFieldsEndpoint}/growth-stages`;
    return this.http
      .get<GrowthStageRegistrationsResponse>(url, {
        params: {
          cropFieldIds: cropFieldIds.map((id) => id.toString()),
          cropYear: cropYear.toString(),
        },
        ...signalStoreCustomerRequestOptions(customerId),
      })
      .pipe(map((response) => response.growthStageRegistrations));
  }

  private formatDate(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  }
}
